import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EnvelopeSimple, ArrowLeft, Check } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

export default function ForgotPassword() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post(
        "/auth/forgot-password",
        { email },
        { headers: { "Accept-Language": lang } },
      );
      setSent(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") setErr(detail);
      else setErr(lang === "da" ? "Noget gik galt. Prøv igen." : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-zinc-400 hover:text-white mb-8"
        >
          <ArrowLeft weight="bold" size={14} /> {lang === "da" ? "Tilbage til login" : "Back to login"}
        </Link>

        <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center mb-6">
          <span className="font-display text-base text-white">TF</span>
        </div>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="label-overline text-[#34C759] mb-3">
              {lang === "da" ? "Tjek din mail" : "Check your inbox"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              {lang === "da" ? "Mail på vej." : "Email sent."}
            </h1>
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
              {lang === "da" ? (
                <>Hvis <strong className="text-white">{email}</strong> findes hos os, har du en mail med et link til at nulstille din adgangskode. Linket virker i 30 minutter.</>
              ) : (
                <>If <strong className="text-white">{email}</strong> matches an account, you'll receive an email with a link to reset your password. The link works for 30 minutes.</>
              )}
            </p>
            <div className="tf-card p-4 mt-6 flex gap-3 items-start">
              <Check weight="bold" size={18} color="#34C759" className="shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-400 leading-relaxed">
                {lang === "da"
                  ? "Tjek også din spam-mappe hvis du ikke kan se den. Du kan trygt prøve igen om 15 minutter."
                  : "Also check your spam folder if you don't see it. You can try again in 15 minutes if needed."}
              </div>
            </div>
            <button
              type="button"
              data-testid="forgot-back-to-login"
              onClick={() => nav("/login")}
              className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] text-white px-4 py-3 font-bold uppercase tracking-widest text-sm mt-6"
            >
              {lang === "da" ? "Tilbage til login" : "Back to sign in"}
            </button>
          </div>
        ) : (
          <>
            <div className="label-overline text-[#007AFF] mb-3">
              {lang === "da" ? "Nulstil adgangskode" : "Reset password"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              {lang === "da" ? "Glemt din kode?" : "Forgot your password?"}
            </h1>
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
              {lang === "da"
                ? "Indtast din e-mail, så sender vi dig et link til at vælge en ny adgangskode."
                : "Enter your email and we'll send you a link to choose a new password."}
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-3">
              <div className="flex items-center gap-3 border border-[#262626] bg-[#141414] px-3 py-3 focus-within:border-[#007AFF]">
                <EnvelopeSimple size={16} weight="duotone" className="text-zinc-500" />
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={lang === "da" ? "Din e-mail" : "Your email"}
                  className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500"
                />
              </div>
              {err && (
                <div
                  data-testid="forgot-error"
                  className="text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2"
                >
                  {err}
                </div>
              )}
              <button
                type="submit"
                data-testid="forgot-submit"
                disabled={loading}
                className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-60 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {loading
                  ? (lang === "da" ? "Sender…" : "Sending…")
                  : (lang === "da" ? "Send nulstillingslink" : "Send reset link")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
